'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseCardProps } from '../cards/CourseCard.types';
import {
  AiHighlightsBlock,
  Breadcrumb,
  CareerBlock,
  ChapterTree,
  CourseLayout,
  CourseTabs,
  CourseTitleBlock,
  FaqAccordion,
  InstructorCard,
  OutcomesList,
  PriceCard,
  RatingHistogram,
  RelatedCourses,
  ResourceList,
  ReviewCard,
  SectionShell,
  SkillChips,
  StatRow,
  type CourseDetailProps,
  type TabSpec,
} from './shared';

// CourseDetailContentFirst — the DEFAULT.
//
// "What's actually inside this course?"
//
// The curriculum is the page's primary argument. It opens by default
// (`defaultOpen`) so every chapter and lesson is visible without
// clicking. Above the tabs: a compact hero + the preview video. Below
// the tabs: tabs for everything else.
//
// Tab order (from data-richest to data-sparsest, so the default tab
// is always the most informative):
//   curriculum → outcomes → skills+career → instructor → reviews →
//   faq → resources

interface ContentFirstProps extends CourseDetailProps {
  /** Theme-resolved CourseCard for the Related row. */
  SimilarCard: ComponentType<{ course: CourseCardProps }>;
}

export function CourseDetailContentFirst({
  tenantSlug,
  course,
  related,
  SimilarCard,
  islandSlot,
  enrollmentIsland,
}: ContentFirstProps) {
  const { t } = useTranslation('common');
  const chapters = course.chapters ?? [];
  const firstLessonId = chapters[0]?.lessons?.[0]?.id ?? 'l1';

  const tabs: TabSpec[] = [
    { id: 'curriculum', label: t('title.curriculum') },
    {
      id: 'outcomes',
      label: t('label.learningOutcomes'),
      show: (course.outcomes?.length ?? 0) > 0,
    },
    {
      id: 'career',
      label: t('label.skillsAndCareer'),
      show: Boolean(course.skills?.length || course.careerOutcomes),
    },
    { id: 'instructor', label: t('label.trainer') },
    { id: 'reviews', label: t('label.comments'), show: Boolean(course.reviewSummary) },
    { id: 'faq', label: t('label.FAQ'), show: (course.faqs?.length ?? 0) > 0 },
    { id: 'resources', label: t('label.documents'), show: (course.resources?.length ?? 0) > 0 },
  ];

  return (
    <CourseLayout
      hero={
        <header className="cd-hero">
          <Breadcrumb
            tenantSlug={tenantSlug}
            categorySlug={course.category}
            categoryName={course.categoryName}
            courseTitle={course.title}
          />
          <CourseTitleBlock course={course} />
          <div style={{ marginTop: 16, marginBottom: 24 }}>
            <InstructorCard instructor={course.instructor} size="sm" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <StatRow
              course={course}
              show={['rating', 'enrolled', 'duration', 'lessons', 'language', 'updated']}
            />
          </div>
          {/* Phase 5 — island slot. Renders Progress + ContinueLearning
              + Wishlist + Review composer for enrolled users; renders
              nothing (cleanly) for anonymous viewers. Sits between the
              StatRow and the preview-video reminder so it's visible
              above the fold for enrolled users coming back to resume. */}
          {islandSlot && (
            <div
              style={{
                marginBottom: 20,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
              }}
            >
              {islandSlot}
            </div>
          )}
          {/* Preview video lives in the PriceCard's preview thumbnail.
              Rendering it here too would duplicate the same content
              on the same page — confusing UX. The PriceCard sits in
              the sidebar at the same grid row as the hero, so it's
              visually next to the hero (and protrudes below it). */}
        </header>
      }
      main={
        <CourseTabs
          tabs={tabs}
          defaultTabId="curriculum"
          panels={{
            curriculum: (
              <SectionShell title={t('title.curriculum')} subtitle={t('label.curriculumSubtitle')}>
                {/* AI summary above the curriculum — primes the user
                    with key insights before they scan a long chapter
                    tree. Renders nothing if aiHighlights is empty. */}
                {(course.aiHighlights?.length ?? 0) > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <AiHighlightsBlock highlights={course.aiHighlights} />
                  </div>
                )}
                <ChapterTree
                  chapters={chapters}
                  defaultOpen
                  tenantSlug={tenantSlug}
                  courseId={course.id}
                />
              </SectionShell>
            ),
            outcomes: (course.outcomes?.length ?? 0) > 0 && (
              <SectionShell title={t('label.whatYouWillLearn')}>
                <OutcomesList outcomes={course.outcomes ?? []} />
              </SectionShell>
            ),
            career: (course.skills?.length || course.careerOutcomes) && (
              <SectionShell title={t('label.skillsAndCareer')}>
                {course.skills && course.skills.length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <SkillChips skills={course.skills} />
                  </div>
                )}
                {course.careerOutcomes && <CareerBlock career={course.careerOutcomes} />}
              </SectionShell>
            ),
            instructor: (
              <SectionShell title={t('label.trainer')}>
                <InstructorCard instructor={course.instructor} size="lg" />
              </SectionShell>
            ),
            reviews: course.reviewSummary && (
              <SectionShell title={t('label.comments')}>
                <RatingHistogram
                  rating={course.ratingAverage}
                  ratingCount={course.ratingCount}
                  distribution={course.reviewSummary.distribution}
                />
                <div style={{ display: 'grid', gap: 12 }}>
                  {course.reviewSummary.featured.map((r, i) => (
                    <ReviewCard key={i} review={r} />
                  ))}
                </div>
              </SectionShell>
            ),
            faq: (course.faqs?.length ?? 0) > 0 && (
              <SectionShell title={t('title.faq')}>
                <FaqAccordion faqs={course.faqs ?? []} />
              </SectionShell>
            ),
            resources: (course.resources?.length ?? 0) > 0 && (
              <SectionShell title={t('label.documents')}>
                <ResourceList resources={course.resources ?? []} />
              </SectionShell>
            ),
          }}
        />
      }
      sidebar={
        <PriceCard
          course={course}
          tenantSlug={tenantSlug}
          firstLessonId={firstLessonId}
          enrollAction={enrollmentIsland}
        />
      }
      full={
        related.length > 0 && (
          <SectionShell title={t('title.similarLearnings')}>
            <RelatedCourses courses={related} Card={SimilarCard} />
          </SectionShell>
        )
      }
    />
  );
}
